import { Injectable } from '@angular/core';
import { CPConfiguration } from '@configuration-client';
import { Settings } from '@ct-client';
import { AppInitializerService } from '../../app.initializer';

@Injectable({
    providedIn: 'root',
})
export class SecretService {
    private nAcctID: number;
    private configuration: CPConfiguration; // configuration-service/api/config/cp/<portal-config>
    private portalSettings: Settings; // <ct-host>/api/cp/customerPortal/mdSettings/getSettings

    private orderVersion: string;

    constructor(private appInitializerService: AppInitializerService) {
        this.nAcctID = 0;
    }

    public getTokenPayload() {
        return this.getToken() ? this.appInitializerService.getTokenPayload(this.getToken()) : null;
    }

    public setConfiguration(configuration: CPConfiguration): void {
        this.configuration = configuration;
    }

    public setPortalSettings(settings: Settings): void {
        this.portalSettings = settings;
    }

    public getConfigID() {
        return this.configuration ? this.configuration.configId : null;
    }

    public getPortalID() {
        return this.configuration
            ? !!this.configuration.alias && !!this.configuration.alias.find((s) => s === location.hostname)
                ? 'cup'
                : this.configuration.configId
            : null;
    }

    public getToken(): string {
        return this.appInitializerService.getToken() ? this.appInitializerService.getToken().access_token : null;
    }

    public getUser(): any {
        const payload = this.getTokenPayload();
        const user = payload ? payload['https://supplystack.com/user_basic'] : null;
        const preferences = payload ? payload['https://supplystack.com/user_metadata'] : null;
        if (user) {
            const portals = this.getUserPortals(payload);
            user.portalConfigs = portals;
        }
        if (!!user && !!preferences) {
            user.preferences = preferences.userPreferences;
        }
        return payload ? payload['https://supplystack.com/user_basic'] : null;
    }

    public getUserPortals(payload): any {
        const portalList = payload ? JSON.parse(payload['https://supplystack.com/portalConfigs']) : [];
        return portalList.filter((portal) => portal.type === 'CP');
    }

    public hasCurrentUserAccessToPortal(): boolean {
        const user = this.getUser();
        if (!user) {
            return false;
        }
        const portalID = this.getConfigID();
        if (!portalID) {
            return false;
        }
        return this.hasUserAccessToPortal(user, portalID);
    }

    public hasUserAccessToPortal(user, portalID: string): boolean {
        if (!user) {
            return false;
        }
        if (!portalID) {
            return false;
        }
        return !!user.portalConfigs.find(p => p.type === 'CP' && p.name === portalID);
    }

    public getNAcctID(): number {
        return this.nAcctID;
    }

    public getConfiguration(): CPConfiguration {
        return this.configuration;
    }

    public getPortalSettings(): Settings {
        return this.portalSettings;
    }

    public logout() {
        this.nAcctID = undefined;
        this.appInitializerService.logout(this.getSettings().sAppTheme);
    }

    private getSettings(): any {
        return this.getPortalSettings() ? this.getPortalSettings() : {};
    }

    public setOrderFlowVersion(version: string) {
        this.orderVersion = version;
    }

    public getOrderFlowVersion() {
        return this.orderVersion;
    }

    public isVersionHigherOrEqual(currentVersion: string, targetVersion: string): boolean {
        if (!currentVersion) {
            return false;
        }
        
        const current = this.parseVersion(currentVersion);
        const target = this.parseVersion(targetVersion);
        
        return (current.major > target.major) ||
               (current.major === target.major && current.minor > target.minor) ||
               (current.major === target.major && current.minor === target.minor && current.patch >= target.patch);
    }

    private parseVersion(version: string): { major: number, minor: number, patch: number } {
        // Remove -RC suffix if present
        const cleanVersion = version.replace(/-RC$/, '');
        const parts = cleanVersion.split('.');
        return {
            major: parseInt(parts[0], 10) || 0,
            minor: parseInt(parts[1], 10) || 0,
            patch: parseInt(parts[2], 10) || 0
        };
    }
}

export enum OrderFlowVersion {
    V1 = 'v1',
    V2 = 'v2',
}
